import { useRef, useState } from "react";
import { ApiError, type NotificationSubscriptionDto, type SetSubscriptionRequest } from "@keydials/api-client";
import { type HelpAnchor } from "@keydials/ui";
import { Icon } from "../components/Icon";
import { AnchoredMenu } from "../components/AnchoredMenu";
import { useWorkspaceServices, useCapabilities } from "../context";
import {
  activeChannels,
  channelBlockedReason,
  channelOn,
  followedThroughGroup,
  isInherited,
  isSubscribed,
  offeredChannels,
  planChannel,
  planToggleSubscribed,
  type Channel,
  type Scope,
} from "../lib/subscriptions";

/**
 * Subscribe control for a scope (whole dial / section / segment). The button opts you in (or out) at this
 * scope; the caret opens a dropdown to choose channels. Subscribing to a broader scope covers everything
 * below it, and you can mute an individual section/segment here as an exception.
 *
 * `variant: "icon"` collapses the labelled button + separate caret into one bell button, for placements
 * (card corners) too tight for the full control — the same dropdown opens, with the subscribe/unsubscribe
 * toggle folded in as its first entry so nothing the full variant offers is lost.
 */
export function SubscribeButton({
  instanceId,
  scope,
  subs,
  onChanged,
  scopeLabel,
  meHasPhoneNumber = false,
  helpAnchorId,
  variant = "full",
}: {
  instanceId: string;
  scope: Scope;
  subs: NotificationSubscriptionDto[];
  onChanged: () => void | Promise<void>;
  /** e.g. "this dial" / "this section" / "this action point" — for the dropdown heading + titles. */
  scopeLabel: string;
  /** Whether the current user has a phone number on file. SMS can only be switched on when they do
   * (KEY-453) — defaults to false so a host that can't answer gates it rather than promising delivery. */
  meHasPhoneNumber?: boolean;
  /** Optional anchor id so a guided-help tour can spotlight this particular subscribe control. */
  helpAnchorId?: HelpAnchor;
  /** "full" (default) is the labelled button + caret. "icon" is a single bell button — see above. */
  variant?: "full" | "icon";
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  // In preview mode the control is shown but inert (no subscriptions to persist).
  const capabilities = useCapabilities();
  const previewDisabled = !capabilities.subscriptions;
  const pushEnabled = !!capabilities.pushNotifications;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The server's own words when it refused a channel (KEY-684: Push with no phone registered, or Push switched
  // off since the page loaded). Shown under that channel's row, where the SMS no-phone note sits (KEY-453) —
  // we can't know in advance whether this person has a phone signed in to the app, so the refusal IS the answer.
  const [refusal, setRefusal] = useState<{ channel: Channel; message: string } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Click-away lives in AnchoredMenu now (KEY-532). This component used to do its own, keyed on the wrapper
  // — which stopped working the moment the menu was portalled out of it: every press on a menu row counted
  // as "outside", so the menu unmounted on mousedown and the row's click never landed. It looked exactly
  // like a menu that had been used, which is why it needed a DOM probe to find.

  const subscribed = isSubscribed(subs, scope);
  const inherited = subscribed && isInherited(subs, scope);
  // KEY-592: a follow on a group above this dial reaches it too — say which group, since it is set over there.
  const viaGroup = subscribed ? followedThroughGroup(subs, scope) : null;
  const active = activeChannels(subs, scope);
  const iconAriaLabel = `${subscribed ? "Subscribed to" : "Not subscribed to"} ${scopeLabel} — notification options`;

  async function apply(requests: SetSubscriptionRequest[], channel?: Channel) {
    const token = getAccessToken();
    if (!token || busy || requests.length === 0 || previewDisabled) {
      return;
    }
    setBusy(true);
    setRefusal(null);
    try {
      for (const request of requests) {
        await api.setSubscription(token, instanceId, request);
      }
      await onChanged();
    } catch (e) {
      // A 400 on a single channel carries the server's reason (it names what to do about it); say it under the
      // row. Anything else stays best-effort: a reload via onChanged resyncs once a later toggle succeeds.
      if (channel && e instanceof ApiError && e.status === 400) {
        setRefusal({ channel, message: e.message });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", opacity: previewDisabled ? 0.55 : 1 }}>
      {variant === "icon" ? (
        <button
          onClick={() => setOpen((o) => !o)}
          disabled={busy || previewDisabled}
          data-help-anchor={helpAnchorId}
          aria-label={iconAriaLabel}
          aria-expanded={open}
          title={previewDisabled ? "Subscriptions aren't available in preview" : iconAriaLabel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            flex: "none",
            borderRadius: "var(--radius-sm)",
            border: `var(--border-w) solid ${subscribed ? "var(--accent-border)" : "var(--border)"}`,
            background: subscribed ? "var(--accent-soft)" : "var(--surface)",
            cursor: busy ? "default" : "pointer",
            color: subscribed ? "var(--accent-text)" : "var(--text-muted)",
          }}
        >
          <Icon name={subscribed ? "bell" : "bellOff"} size={14} />
        </button>
      ) : (
        <div
          style={{
            display: "inline-flex",
            alignItems: "stretch",
            borderRadius: "var(--radius-sm)",
            border: `var(--border-w) solid ${subscribed ? "var(--accent-border)" : "var(--border)"}`,
            background: subscribed ? "var(--accent-soft)" : "var(--surface)",
            overflow: "hidden",
          }}
        >
          <button
            onClick={() => void apply(planToggleSubscribed(subs, scope))}
            disabled={busy || previewDisabled}
            data-help-anchor={helpAnchorId}
            title={previewDisabled ? "Subscriptions aren't available in preview" : subscribed ? `Unsubscribe from ${scopeLabel}` : `Subscribe to ${scopeLabel}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 34,
              padding: "0 11px",
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? "default" : "pointer",
              border: "none",
              background: "none",
              color: subscribed ? "var(--accent-text)" : "var(--text-muted)",
            }}
          >
            <Icon name={subscribed ? "bell" : "bellOff"} size={15} />
            {subscribed ? "Subscribed" : "Subscribe"}
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            disabled={busy || previewDisabled}
            aria-label="Notification channels"
            aria-expanded={open}
            title={previewDisabled ? "Subscriptions aren't available in preview" : "Choose channels"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              border: "none",
              borderLeft: `var(--border-w) solid ${subscribed ? "var(--accent-border)" : "var(--border)"}`,
              background: "none",
              cursor: "pointer",
              color: subscribed ? "var(--accent-text)" : "var(--text-faint)",
            }}
          >
            <Icon name="chevDown" size={14} />
          </button>
        </div>
      )}

      {/* Portalled (KEY-532), for the same reason as StatusPicker: the Dynamic workspace scrolls the card
          stack, and any overflow but `visible` clips every descendant. */}
      <AnchoredMenu anchorRef={ref} open={open} onDismiss={() => setOpen(false)} minWidth={230} zIndex={50}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", padding: "4px 8px 6px" }}>
            Notify me about {scopeLabel}
          </div>
          {variant === "icon" && (
            <>
              <button
                onClick={() => void apply(planToggleSubscribed(subs, scope))}
                disabled={busy}
                className={busy ? undefined : "kd-row-hover"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "8px 8px",
                  border: "none",
                  background: "none",
                  borderRadius: "var(--radius-sm)",
                  cursor: busy ? "default" : "pointer",
                  font: "inherit",
                  fontSize: 13.5,
                  fontWeight: 700,
                  textAlign: "left",
                  color: "var(--text)",
                }}
              >
                <Icon name={subscribed ? "bell" : "bellOff"} size={15} style={{ color: subscribed ? "var(--accent-text)" : "var(--text-faint)" }} />
                {subscribed ? `Unsubscribe from ${scopeLabel}` : `Subscribe to ${scopeLabel}`}
              </button>
              <div style={{ height: 1, background: "var(--border)", margin: "4px 4px 6px" }} />
            </>
          )}
          {offeredChannels(subs, scope, pushEnabled).map((c) => {
            const on = channelOn(subs, scope, c.key);
            const blocked = channelBlockedReason(c.key, meHasPhoneNumber);
            const note = blocked ?? (refusal?.channel === c.key ? refusal.message : null);
            // One-way: a blocked channel can still be switched OFF, so an opt-in made before the account
            // lost (or never had) a phone number isn't stuck delivering nothing.
            const rowDisabled = busy || (!!blocked && !on);
            return (
              <div key={c.key}>
                <button
                  onClick={() => void apply([planChannel(subs, scope, c.key, !on)], c.key)}
                  disabled={rowDisabled}
                  title={blocked ?? undefined}
                  className={rowDisabled ? undefined : "kd-row-hover"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "8px 8px",
                    border: "none",
                    background: "none",
                    borderRadius: "var(--radius-sm)",
                    cursor: rowDisabled ? "default" : "pointer",
                    font: "inherit",
                    fontSize: 13.5,
                    textAlign: "left",
                    color: "var(--text)",
                    opacity: blocked && !on ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      flex: "none",
                      borderRadius: 5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `var(--border-w) solid ${on ? "var(--accent-border)" : "var(--border)"}`,
                      background: on ? "var(--accent)" : "var(--surface)",
                      color: "var(--on-accent, #fff)",
                    }}
                  >
                    {on && <Icon name="check" size={12} />}
                  </span>
                  {c.label}
                </button>
                {note && (
                  <div
                    role={blocked ? undefined : "alert"}
                    style={{ fontSize: 11.5, color: "var(--text-faint)", padding: "0 8px 6px 36px", lineHeight: 1.4 }}
                  >
                    {note}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ fontSize: 11.5, color: "var(--text-faint)", padding: "6px 8px 2px", lineHeight: 1.4 }}>
            {active.size === 0
              ? `You won't be notified about ${scopeLabel}.`
              : viaGroup
                ? `You follow this through ${viaGroup} — turning a channel off mutes ${scopeLabel} only.`
                : inherited
                  ? "Inherited from a broader subscription — turning a channel off mutes it just here."
                  : null}
          </div>
          {/* KEY-592: what a subscription is for, now that it no longer carries the chat. */}
          <div style={{ fontSize: 11.5, color: "var(--text-faint)", padding: "4px 8px 2px", lineHeight: 1.4 }}>
            Covers status changes and review dates. Messages reach the people they @mention.
          </div>
      </AnchoredMenu>
    </div>
  );
}
